import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LionsStripe from '../components/LionsStripe';
import { UserIcon } from '@heroicons/react/24/outline';

const QuoteIcon = () => (
  <svg className="w-8 h-8 text-secondary/40" fill="currentColor" viewBox="0 0 24 24">
    <path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C19.5693 16 20.017 15.5523 20.017 15V9C20.017 8.44772 19.5693 8 19.017 8H15.017C14.4647 8 14.017 8.44772 14.017 9V11C14.017 11.5523 13.5693 12 13.017 12H12.017V5H22.017V15C22.017 18.3137 19.3307 21 16.017 21H14.017ZM5.0166 21L5.0166 18C5.0166 16.8954 5.91198 16 7.0166 16H10.0166C10.5689 16 11.0166 15.5523 11.0166 15V9C11.0166 8.44772 10.5689 8 10.0166 8H6.0166C5.46432 8 5.0166 8.44772 5.0166 9V11C5.0166 11.5523 4.56889 12 4.0166 12H3.0166V5H13.0166V15C13.0166 18.3137 10.3303 21 7.0166 21H5.0166Z" />
  </svg>
);

const NEWS_VIDEOS = [
  {
    src: '/assets/videos/principal/teacher-appreciation-week-fox5-part-1.mp4',
    poster: '/assets/videos/principal/teacher-appreciation-week-fox5-part-1-poster.jpg',
    title: 'Part 1: Live from The Lexington Academy',
    description: 'FOX 5 goes live from our schoolyard to celebrate Teacher Appreciation Week, joined by Dr. Smith.',
    duration: '2:54'
  },
  {
    src: '/assets/videos/principal/teacher-appreciation-week-fox5-part-2.mp4',
    poster: '/assets/videos/principal/teacher-appreciation-week-fox5-part-2-poster.jpg',
    title: 'Part 2: A Surprise in Our Auditorium',
    description: 'Students and staff gather in the auditorium as DonorsChoose surprises our teachers with funding for their classrooms.',
    duration: '3:46'
  }
];

const PrincipalsMessagePage = () => {
  return (
    <div className="min-h-screen bg-accent-light font-sans selection:bg-secondary selection:text-white">
      <Navbar />

      <main id="main-content">
        {/* ============================================================================
            HERO SECTION
        ============================================================================ */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-primary overflow-hidden">
          {/* Abstract Background */}
          <div className="absolute inset-0">
             <div className="absolute inset-0 bg-gradient-to-br from-primary via-navy-900 to-primary"></div>
             <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay"></div>
             {/* Decorative elements */}
             <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[100px]"></div>
             <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-[100px]"></div>
          </div>

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center bg-primary rounded-2xl py-1">
            <div className="inline-flex items-center justify-center p-3 bg-white/10 backdrop-blur rounded-full mb-8 border border-white/20">
               <UserIcon className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
              Principal's <span className="text-secondary">Message</span>
            </h1>
            <p className="text-lg text-white/80 font-light max-w-2xl mx-auto">
              A letter from Dr. Tracy Smith
            </p>
          </div>
          <LionsStripe className="absolute inset-x-0 bottom-0 z-10" />
        </section>

        {/* ============================================================================
            LETTER SECTION
        ============================================================================ */}
        <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 -mt-12 relative z-20">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl p-8 md:p-16 relative overflow-hidden">
              {/* Decorative top border */}
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-primary via-secondary to-primary"></div>
              
              {/* Watermark */}
              <div className="absolute top-10 right-10 opacity-5 pointer-events-none">
                <img src="/assets/images/logo.png" alt="" className="w-64 h-64 object-contain grayscale" />
              </div>

              <div className="relative z-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-8 font-serif">
                  Welcome to Lexington Academy.
                </h2>

                <div className="space-y-6 text-lg text-gray-700 leading-relaxed font-light">
                  <p>
                    It is my privilege to serve as Principal of Lexington Academy; a distinguished institution committed to the academic and personal growth of students from 3K through 8th grade. Our school is dedicated to cultivating excellence, fostering innovation, and preparing students to meet the challenges of an ever-changing world with confidence and integrity.
                  </p>
                  <p>
                    Lexington Academy is proud to offer a Dual Language Program for grades K–5, designed to equip students with bilingual proficiency and cultural competency. This program reflects our belief that multilingual education is a powerful tool for broadening perspectives and opening doors to future opportunities.
                  </p>
                  <p>
                    In addition to our rigorous academic curriculum, we provide an in-house academic enrichment after-school program that reinforces classroom learning while encouraging exploration and creativity. This program ensures that students receive the support and resources necessary to excel academically and personally.
                  </p>
                  <p>
                    We also recognize the importance of extracurricular engagement in shaping well-rounded individuals. Our offerings include cheerleading, basketball, drumline, and soccer, each providing students with opportunities to develop teamwork, discipline, and school spirit.
                  </p>
                  <p className="font-medium text-primary">
                    At Lexington Academy, we remain steadfast in our commitment to educating the whole child. We strive to create an environment where every student is valued, challenged, and inspired to reach their fullest potential.
                  </p>
                  <p>
                    We invite you to partner with us in this mission and to share in the success of our students as they grow into thoughtful, capable, and responsible citizens.
                  </p>
                </div>

                <div className="mt-12 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                  <div className="w-32 h-32 bg-gray-100 rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-md">
                     <img
                       src="/assets/images/Dr. Tracy Smith, Principal.jpg"
                       alt="Dr. Tracy Smith"
                       className="w-full h-full object-cover"
                     />
                  </div>
                  <div>
                    <p className="text-gray-600 text-sm italic mb-1">Warm regards,</p>
                    <h3 className="text-xl font-bold text-gray-900 font-serif">Dr. Tracy Smith</h3>
                    <p className="text-primary font-medium text-sm uppercase tracking-wider mt-1">Principal, Lexington Academy</p>
                  </div>
                </div>
              </div>
            </div>

            {/* In the News: Teacher Appreciation Week videos. preload="none" so
                nothing downloads until a visitor presses play. */}
            <section aria-labelledby="in-the-news-heading" className="mt-12">
              <span className="text-orange-700 font-bold tracking-widest text-sm uppercase mb-2 block">
                In the News
              </span>
              <h2 id="in-the-news-heading" className="text-2xl md:text-3xl font-bold text-gray-900">
                Celebrating Teacher Appreciation Week
              </h2>
              <p className="mt-2 mb-8 text-lg text-gray-700 font-light">
                FOX 5&rsquo;s <em>Good Day New York</em> visited The Lexington Academy to celebrate our
                teachers.
              </p>

              <div className="space-y-8">
                {NEWS_VIDEOS.map((video) => (
                  <figure
                    key={video.src}
                    className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden"
                  >
                    <video
                      controls
                      playsInline
                      preload="none"
                      poster={video.poster}
                      className="w-full aspect-video bg-black"
                    >
                      <source src={video.src} type="video/mp4" />
                      <p className="p-6 text-white">
                        Your browser can&rsquo;t play this video.{' '}
                        <a href={video.src} className="underline">Download it instead</a>.
                      </p>
                    </video>
                    <figcaption className="p-6 sm:p-8">
                      <h3 className="text-lg font-bold text-gray-900">{video.title}</h3>
                      <p className="mt-1 text-gray-700">{video.description}</p>
                      <p className="mt-2 text-sm text-gray-600">Video &middot; {video.duration}</p>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </section>

            {/* Quote Card */}
            <div className="mt-12 bg-primary text-white rounded-2xl p-10 text-center relative overflow-hidden shadow-lg">
              <div className="absolute top-4 left-4 opacity-20">
                <QuoteIcon />
              </div>
              <blockquote className="relative z-10">
                <p className="text-2xl md:text-3xl font-serif italic mb-4">
                  "The Strength of the Future"
                </p>
                <footer className="text-sm font-medium uppercase tracking-widest text-white/70">
                  Our School Motto
                </footer>
              </blockquote>
            </div>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PrincipalsMessagePage;
